import { useQuery } from '@tanstack/react-query';
import { legacy } from '@/shared/api/client';
import { catalogCost, type CatalogProduct } from '../model/operator-payload';
import type { OperatorProgram } from '../model/operator-rules';
import type { Product } from '@/shared/types/product';

function headers(idPassbook: string) {
  return { headers: { 'X-Passbook-Id': idPassbook } };
}

function isEnabled(value: unknown): boolean {
  if (value === false || value === 'false') return false;
  return true;
}

/** getByPoints: redemption !== false (undefined/null/true passam). */
function isRedemptionProduct(pr: Product): boolean {
  return pr.redemption !== false && pr.redemption !== ('false' as unknown as boolean);
}

/** Sem redeemType no passbook: product + discount (courtesy/birthday ficam de fora). */
export function isOperatorCatalogType(redemptionType?: string | null): boolean {
  const type = (redemptionType ?? 'product').toLowerCase();
  return type === 'product' || type === 'discount';
}

/** Com passbook.redeemType: só produtos desse tipo (legado wallet). */
export function matchesPassbookRedeemType(
  productRedemptionType: string | null | undefined,
  passbookRedeemType?: string | null,
): boolean {
  const wanted = passbookRedeemType?.trim().toLowerCase();
  if (!wanted) return isOperatorCatalogType(productRedemptionType);
  const actual = (productRedemptionType ?? 'product').toLowerCase();
  return actual === wanted;
}

/**
 * Filtro client do catálogo de resgate.
 * Exportado para testes.
 *
 * Semântica Product: `value` = custo na carteira (pts/selos); `price` = R$ de desconto.
 *
 * Alinhamento wallet (`user.js` ~3718):
 * `Product.find({ redemptionType: redeemType, idPassbook })` — **não** exige
 * `redemption: true`. Vouchers de selos/discount costumam vir com redemption false.
 * Programa `stamps` também ignora o boolean (mesmo sem redeemType no passbook).
 *
 * Sem redeemType (pontos/cashback): espelha getByPoints — redemption ≠ false + product|discount.
 * Pontos/cashback: custo ≤ saldo. Selos: lista o redeemType inteiro; custo no confirmar.
 */
export function filterOperatorCatalogProducts(
  list: Product[],
  opts: {
    floorPts: number;
    /** Se setado, só products com redemptionType === redeemType (legado). */
    redeemType?: string | null;
    program?: OperatorProgram;
    /** passbook.usesProductValueFieldAsDiscount — value vira R$ em produtos discount, não custo. */
    usesValueAsDiscount?: boolean;
    /** passbook.maxInputValidation — custo em selos do resgate quando usesValueAsDiscount. */
    maxInputValidation?: number;
  },
): CatalogProduct[] {
  const skipBalanceCostFilter = opts.program === 'stamps';
  const hasRedeemType = Boolean(opts.redeemType?.trim());
  // Wallet com redeemType ignora o boolean `redemption`. Selos idem: vouchers
  // discount costumam estar com redemption:false no Mongo.
  const skipRedemptionFlag = hasRedeemType || opts.program === 'stamps';
  const costCtx = {
    usesProductValueFieldAsDiscount: opts.usesValueAsDiscount,
    maxInputValidation: opts.maxInputValidation,
  };

  return list
    .filter((pr) => (skipRedemptionFlag ? true : isRedemptionProduct(pr)))
    .filter((pr) => isEnabled((pr as { enabled?: unknown }).enabled))
    .filter((pr) => matchesPassbookRedeemType(pr.redemptionType, opts.redeemType))
    .map((pr) => mapCatalogRow(pr as Product & { enabled?: boolean | string }))
    .filter((pr) => {
      const cost = catalogCost(pr, costCtx);
      if (cost < 0) return false;
      if (skipBalanceCostFilter) return true;
      return cost <= opts.floorPts + 1e-9;
    });
}

function mapCatalogRow(pr: Product & { points?: number | string; enabled?: boolean | string }): CatalogProduct {
  const value = pr.value ?? pr.points;
  return {
    _id: pr._id,
    name: pr.name,
    value,
    points: typeof value === 'number' ? value : parseFloat(String(value ?? 0)) || 0,
    price: pr.price,
    sku: pr.uniqueId,
    redemptionType: pr.redemptionType,
    enabled: pr.enabled as boolean | string | undefined,
  };
}

function normalizeList(res: Product[] | { data?: Product[]; products?: Product[] }): Product[] {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.products)) return res.products;
  return [];
}

/**
 * Catálogo de resgate.
 * Sempre GET /products/:id/true (como a página Produtos) + filtro client:
 * redeemType do passbook; custo ≤ saldo em pontos/cashback (selos: ver filtro).
 */
export function useCatalogProducts(
  idPassbook: string,
  points: number,
  enabled: boolean,
  program?: OperatorProgram,
  redeemType?: string | null,
  usesValueAsDiscount?: boolean,
  maxInputValidation?: number,
) {
  const floorPts = Math.max(0, Math.floor(points));
  const redeemKey = redeemType?.trim().toLowerCase() || 'default';

  return useQuery({
    // v3: bust cache de filtros que exigiam redemption:true com redeemType
    queryKey: [
      'operator',
      'catalog',
      'v3',
      idPassbook,
      floorPts,
      redeemKey,
      program ?? '',
      Boolean(usesValueAsDiscount),
      maxInputValidation ?? '',
    ],
    enabled,
    queryFn: async (): Promise<CatalogProduct[]> => {
      const res = await legacy.get<Product[] | { data?: Product[]; products?: Product[] }>(
        `/products/${idPassbook}/true`,
        headers(idPassbook),
      );
      const list = normalizeList(res);
      return filterOperatorCatalogProducts(list, {
        floorPts,
        redeemType,
        program,
        usesValueAsDiscount,
        maxInputValidation,
      });
    },
    // Tela de operador: nunca serve catálogo/saldo velho — sempre busca de novo.
    staleTime: 0,
    gcTime: 0,
  });
}

/**
 * SKUs que pontuam (redemption: false) — GET /productsNotRedemption/:id
 * (user.js regra-por-produto cruza details com Product.findOne).
 */
export function useAwardableProducts(idPassbook: string, enabled: boolean) {
  return useQuery({
    queryKey: ['operator', 'awardable-products', idPassbook],
    enabled,
    queryFn: async (): Promise<Product[]> => {
      const res = await legacy.get<Product[]>(`/productsNotRedemption/${idPassbook}/`, headers(idPassbook));
      return Array.isArray(res) ? res : [];
    },
    // Tela de operador: nunca serve catálogo/saldo velho — sempre busca de novo.
    staleTime: 0,
    gcTime: 0,
  });
}
